import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import {
  UsersThree, Plus, CircleNotch, ArrowRight, FolderSimple, WarningCircle,
} from "@phosphor-icons/react";

export default function ClientGroups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await api.get("/client-groups");
    setGroups(data.groups);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    setError("");
    try {
      const { data } = await api.post("/client-groups", { name: name.trim() });
      setName("");
      await load();
      navigate(`/client-groups/${data.group_id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div data-testid="client-groups-page">
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Clients</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Client Groups</h1>
        <p className="mt-3 max-w-2xl text-base text-neutral-600">
          Group related Aroflo clients under one head name — e.g. <span className="font-semibold">Newcastle University</span> —
          then attach every related client to it.
        </p>

        {/* Create group */}
        <form onSubmit={create} className="mt-6 flex flex-wrap items-end gap-3 border border-neutral-200 bg-white p-4 shadow-sm rounded-sm" data-testid="create-group-form">
          <div className="flex-1 min-w-[240px]">
            <label className="mb-1.5 block text-sm font-semibold text-[#0A0A0A]">Group name</label>
            <input
              data-testid="group-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Newcastle University"
              className="w-full rounded-sm border border-neutral-300 bg-white px-3.5 py-2.5 text-base outline-none transition-all duration-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20"
            />
          </div>
          <button
            data-testid="create-group-button"
            type="submit"
            disabled={creating || !name.trim()}
            className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-5 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-50"
          >
            {creating ? <CircleNotch size={18} className="animate-spin" /> : <Plus size={18} weight="bold" />} Create group
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-[#DC2626]" data-testid="create-group-error">{error}</p>}

        {/* Groups grid */}
        <div className="mt-8">
          {!groups ? (
            <div className="flex h-40 items-center justify-center"><CircleNotch size={26} className="animate-spin text-[#0055FF]" /></div>
          ) : groups.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center gap-2 border border-dashed border-neutral-300 rounded-sm text-neutral-500" data-testid="groups-empty">
              <FolderSimple size={28} className="text-neutral-300" />
              No groups yet — create your first one above.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {groups.map((g) => (
                <button
                  key={g.group_id}
                  data-testid={`group-card-${g.group_id}`}
                  onClick={() => navigate(`/client-groups/${g.group_id}`)}
                  className="group flex items-center justify-between border border-neutral-200 bg-white p-5 text-left shadow-sm rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#0055FF] hover:shadow-md"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0A0A0A] text-white">
                      <UsersThree size={22} weight="fill" />
                    </div>
                    <div>
                      <p className="font-heading text-lg font-black tracking-tight text-[#0A0A0A]">{g.name}</p>
                      <p className="text-sm text-neutral-500">{g.client_count} client{g.client_count === 1 ? "" : "s"}</p>
                    </div>
                  </div>
                  <ArrowRight size={18} className="text-neutral-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-[#0055FF]" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
